import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarClock } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import type { QueueScheduledPost } from '@/lib/publishQueues/selectQueueScheduledPosts';
import { QUEUE_ACCOUNT_PLATFORMS } from './queueAccountPlatforms';

const COLLAPSED_COUNT = 5;

type Props = {
  posts: QueueScheduledPost[];
  timezone: string;
  loading: boolean;
  error: boolean;
};

function formatWhen(iso: string, timezone: string, locale: string): string {
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false,
  };
  try {
    return new Intl.DateTimeFormat(locale, { ...options, timeZone: timezone }).format(new Date(iso));
  } catch {
    return new Intl.DateTimeFormat(locale, options).format(new Date(iso));
  }
}

export function QueueUpcomingPosts({
  posts, timezone, loading, error,
}: Props) {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? posts : posts.slice(0, COLLAPSED_COUNT);

  let body;
  if (error) {
    body = <p className="text-sm text-destructive">{t('publishQueues.upcoming.loadError')}</p>;
  } else if (loading && posts.length === 0) {
    body = <Skeleton className="h-16 rounded-lg" />;
  } else if (posts.length === 0) {
    body = <p className="text-sm text-muted-foreground">{t('publishQueues.upcoming.empty')}</p>;
  } else {
    body = (
      <>
        <ol className="space-y-2">
          {visible.map((post) => (
            <li key={post.key} className="flex min-w-0 items-start gap-3 rounded-lg border bg-background/40 p-2.5 text-sm">
              <time dateTime={post.scheduledAt} className="w-24 shrink-0 text-xs tabular-nums text-muted-foreground">
                {formatWhen(post.scheduledAt, timezone, i18n.language)}
              </time>
              <div className="min-w-0 flex-1 space-y-1">
                <p className="truncate" title={post.label ?? undefined}>
                  {post.label ?? t('publishQueues.upcoming.untitled')}
                </p>
                {post.source.kind !== 'queue' ? (
                  <Badge variant="outline" className="max-w-full truncate text-[11px] font-normal">
                    {post.source.kind === 'otherQueue'
                      ? t('publishQueues.upcoming.otherQueue', { name: post.source.queueName })
                      : t('publishQueues.upcoming.manual')}
                  </Badge>
                ) : null}
              </div>
              <span className="flex shrink-0 items-center gap-1">
                {QUEUE_ACCOUNT_PLATFORMS.filter(({ platform }) => post.platforms.includes(platform)).map((platform) => (
                  <span key={platform.platform} title={platform.label} className="flex h-5 w-5 items-center justify-center">
                    {platform.icon}
                  </span>
                ))}
              </span>
            </li>
          ))}
        </ol>
        {posts.length > COLLAPSED_COUNT ? (
          <Button type="button" variant="ghost" size="sm" className="w-full" onClick={() => setExpanded((open) => !open)}>
            {expanded
              ? t('publishQueues.upcoming.showLess')
              : t('publishQueues.upcoming.showAll', { count: posts.length })}
          </Button>
        ) : null}
      </>
    );
  }

  return (
    <div className="min-w-0 space-y-2.5">
      <h3 className="flex items-center gap-2 text-sm font-medium">
        <CalendarClock className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        {t('publishQueues.upcoming.title')}
        {posts.length > 0 ? (
          <span className="font-normal tabular-nums text-muted-foreground">{posts.length}</span>
        ) : null}
      </h3>
      {body}
    </div>
  );
}
